import type React from 'react';
import { forwardRef } from 'react';

import {
  ProductCard as ProductCardTamagoshi,
  type ProductCardProps as TamagoshiProductCardProps,
} from '@/core/tamagoshi/ProductCard/ProductCard';
import { Image } from '@/domains/core/Image/Image';
import {
  CURRENCY,
  PLATFORM,
  PLATFORMS,
} from '@/domains/core/settings/constants';
import type { SponsoredData } from '@/productDiscovery/ProductCard/components/SponsoredDetailedDescription';

import { ProductCardLink } from './components/Link';
import { getProductCardTranslations } from './translations';
import { mapCurrency } from './utils/mapCurrency';

export type ProductCardProps = Omit<
  TamagoshiProductCardProps<typeof ProductCardLink>,
  'translations' | 'currency' | 'imageComponent' | 'tag'
> & {
  url: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  couponAmount?: string;
  multipleImages?: boolean;
  favourite?: boolean;
};

export const ProductCard = forwardRef<HTMLAnchorElement, ProductCardProps>(
  (
    {
      url,
      modelsCount,
      rating,
      ratingCount,
      b2b,
      couponAmount,
      multipleImages,
      imageLoading,
      title,
      imageSrc,
      provideImageDataSrc,
      legalMention = PLATFORM === PLATFORMS.GERMANY,
      favourite = false,
      onAddToCart,
      addToCartState,
      narrow,
      legalMentionNoVAT,
      brandName,
      sellerName,
      adPayer,
      ...otherProps
    },
    ref,
  ) => {
    const { onClick } = otherProps;

    const sponsoredData: SponsoredData = {
      adPayer,
      sellerName: sellerName || '',
      brandName: brandName || '',
    };

    return (
      <ProductCardTamagoshi
        {...otherProps}
        b2b={b2b}
        ref={ref}
        rating={rating}
        ratingCount={ratingCount}
        currency={mapCurrency(CURRENCY)}
        tag={ProductCardLink}
        tagProps={{
          url,
          favourite,
          onClick,
        }}
        legalMention={legalMention}
        legalMentionNoVAT={legalMentionNoVAT}
        translations={getProductCardTranslations(
          modelsCount,
          couponAmount,
          narrow,
          sponsoredData,
        )}
        imageComponent={Image as TamagoshiProductCardProps['imageComponent']}
        modelsCount={modelsCount}
        imageLoading={imageLoading}
        title={title}
        imageSrc={
          !multipleImages && imageSrc.split(',')?.length > 1
            ? imageSrc.split(',')[0]
            : imageSrc
        }
        provideImageDataSrc={provideImageDataSrc}
        onAddToCart={onAddToCart}
        addToCartState={!favourite ? addToCartState : undefined}
        narrow={narrow}
        brandName={brandName}
        sellerName={sellerName}
      />
    );
  },
);

ProductCard.displayName = 'ProductCard';
